import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircleFill, ExclamationTriangle, Image as ImageIcon, Flask, PatchCheckFill, PersonFill, Stars } from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { dayKey } from '../../api/embedMock';

// Shared pieces of the three fit-check embed pages (Statisztika, Látogatók,
// Beállítások): the date range, the test-data badge, copy + toast, cells.

// ── Date range ──────────────────────────────────────────────────────────────

export const RANGE_PRESETS = [7, 30, 90];

export function presetRange(days, today = new Date()) {
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (days - 1));
  return { preset: String(days), from: dayKey(from), to: dayKey(today) };
}

export function DateRangeFilter({ value, onChange }) {
  const { t } = useLocale();
  const today = dayKey(new Date());
  const custom = value.preset === 'custom';

  function setCustom(field, v) {
    if (!v) return;
    const next = { ...value, preset: 'custom', [field]: v };
    // Keep from <= to whichever end moved.
    if (next.from > next.to) {
      if (field === 'from') next.to = next.from;
      else next.from = next.to;
    }
    onChange(next);
  }

  return (
    <div className="emb-range">
      <div className="emb-seg" role="group" aria-label={t('embed.range.label')}>
        {RANGE_PRESETS.map((d) => (
          <button
            key={d}
            type="button"
            className={`emb-seg__btn${value.preset === String(d) ? ' is-active' : ''}`}
            aria-pressed={value.preset === String(d)}
            onClick={() => onChange(presetRange(d))}
          >
            {t('embed.range.days', { n: d })}
          </button>
        ))}
        <button
          type="button"
          className={`emb-seg__btn${custom ? ' is-active' : ''}`}
          aria-pressed={custom}
          onClick={() => onChange({ ...value, preset: 'custom' })}
        >
          {t('embed.range.custom')}
        </button>
      </div>
      {custom && (
        <div className="emb-range__dates">
          <input
            type="date"
            className="emb-input"
            value={value.from}
            max={today}
            aria-label={t('embed.range.from')}
            onChange={(e) => setCustom('from', e.target.value)}
          />
          <span className="emb-range__dash">–</span>
          <input
            type="date"
            className="emb-input"
            value={value.to}
            max={today}
            aria-label={t('embed.range.to')}
            onChange={(e) => setCustom('to', e.target.value)}
          />
        </div>
      )}
    </div>
  );
}

// ── Loading with the previous result held ───────────────────────────────────
// A refetch (new range, filter) keeps the last result on screen, dimmed,
// instead of flashing an empty state.

export function useEmbedLoad(load, deps) {
  const [state, setState] = useState({ data: null, mock: false, loading: true, error: null });
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    load()
      .then((res) => alive && setState({ data: res.data, mock: res.mock, loading: false, error: null }))
      .catch((err) => alive && setState((s) => ({ ...s, loading: false, error: err })));
    return () => {
      alive = false;
    };
    // The caller lists what the load depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

// ── Test-data badge ─────────────────────────────────────────────────────────

export function MockBadge({ show }) {
  const { t } = useLocale();
  if (!show) return null;
  return (
    <span className="emb-mock" title={t('embed.mock.title')}>
      <Flask size={12} aria-hidden="true" />
      {t('embed.mock.badge')}
    </span>
  );
}

export function LoadError({ error }) {
  const { t } = useLocale();
  if (!error) return null;
  const key = error.status === 403 ? 'embed.error.forbidden' : 'embed.error.load';
  return (
    <div className="emb-error" role="alert">
      <ExclamationTriangle size={15} aria-hidden="true" />
      {t(key)}
    </div>
  );
}

// ── Copy + toast ────────────────────────────────────────────────────────────

/// Clipboard API first; the hidden-textarea + execCommand route for where it
/// is missing or refused (plain http, an iframe without clipboard-write).
export async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy route */
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '-1000px';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

/// `copy(text, message)` → copies and shows the portal's floating toast.
export function useCopyToast() {
  const { t } = useLocale();
  const [toast, setToast] = useState(null);
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const copy = useCallback(async (text, message) => {
    const ok = await copyText(text);
    timers.current.forEach(clearTimeout);
    setToast({ ok, message: ok ? message : t('embed.copy.failed'), leaving: false, id: Date.now() });
    timers.current = [
      setTimeout(() => setToast((s) => (s ? { ...s, leaving: true } : s)), 2200),
      setTimeout(() => setToast(null), 2520),
    ];
    return ok;
  }, [t]);

  const element = toast ? (
    <div
      key={toast.id}
      className={`save-toast save-toast--${toast.ok ? 'saved' : 'error'}${toast.leaving ? ' is-leaving' : ''}`}
      role="status"
      aria-live="polite"
    >
      {toast.ok ? <CheckCircleFill size={18} /> : <ExclamationTriangle size={18} />}
      <span>{toast.message}</span>
    </div>
  ) : null;

  return { copy, toast: element };
}

// ── Formatting ──────────────────────────────────────────────────────────────

const LOCALES = { hu: 'hu-HU', en: 'en-GB' };

export function useFormat() {
  const { lang } = useLocale();
  const locale = LOCALES[lang] || 'en-GB';
  return {
    // Never "NaN" / "undefined" on the page: anything not a number is a dash.
    num: (n) => (n == null || !Number.isFinite(Number(n)) ? '—' : Number(n).toLocaleString(locale)),
    money: (amount, currency) => {
      if (amount == null || !Number.isFinite(Number(amount))) return '—';
      try {
        return Number(amount).toLocaleString(locale, { style: 'currency', currency, maximumFractionDigits: currency === 'HUF' ? 0 : 2 });
      } catch {
        return `${Number(amount).toLocaleString(locale)} ${currency || ''}`.trim();
      }
    },
    /// [{ currency, amount }] → one amount per currency, largest first.
    moneyList: (list) => {
      const rows = (Array.isArray(list) ? list : []).filter((r) => r && r.currency && Number.isFinite(Number(r.amount)));
      if (!rows.length) return [];
      return [...rows].sort((a, b) => (b.amount || 0) - (a.amount || 0)).map((r) => ({ key: r.currency, text: Number(r.amount || 0).toLocaleString(locale, { style: 'currency', currency: r.currency, maximumFractionDigits: r.currency === 'HUF' ? 0 : 2 }) }));
    },
    pct: (x, digits = 1) => (x == null || !Number.isFinite(x) ? '—' : `${(x * 100).toLocaleString(locale, { maximumFractionDigits: digits })}%`),
    dateTime: (iso) => (iso ? new Date(iso).toLocaleString(locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'),
    day: (key, opts = { month: 'short', day: 'numeric' }) => {
      const [y, m, d] = String(key).split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(locale, opts);
    },
    ago: (iso) => {
      if (!iso) return '—';
      const diff = Math.max(0, Date.now() - new Date(iso).getTime());
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      const min = Math.round(diff / 60000);
      if (min < 60) return rtf.format(-min, 'minute');
      const h = Math.round(min / 60);
      if (h < 48) return rtf.format(-h, 'hour');
      return rtf.format(-Math.round(h / 24), 'day');
    },
  };
}

// ── Cells ───────────────────────────────────────────────────────────────────

/// `user` is null for a guest (`isGuest`) and wherever the API keeps visitors
/// anonymous (identity off): a neutral avatar and "Vendég" / "Látogató".
/// A named visitor (the API's visitor identity: `displayName`) shows the name,
/// the @username under it and, with `badge`, the "Outfino-felhasználó" mark.
export function initialsOf(user) {
  const words = String(user?.displayName || '').split(/\s+/).map((w) => w.replace(/[^\p{L}\p{N}]/gu, '')).filter(Boolean);
  if (words.length) return words.slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('');
  return String(user?.username || '').replace(/[^\p{L}\p{N}]/gu, '').charAt(0).toUpperCase() || '?';
}

export function UserCell({ user, isGuest, badge = false }) {
  const { t } = useLocale();
  if (!user?.username) {
    return (
      <span className="emb-user emb-user--anon">
        <span className="emb-user__avatar emb-user__avatar--anon" aria-hidden="true"><PersonFill size={15} /></span>
        <span className="emb-user__name">{t(isGuest ? 'embed.visitors.guest' : 'embed.visitors.anonymous')}</span>
      </span>
    );
  }
  const named = !!user.displayName;
  return (
    <span className={`emb-user${named ? ' emb-user--named' : ''}`}>
      {user?.avatarUrl ? (
        <img className="emb-user__avatar" src={user.avatarUrl} alt="" loading="lazy" />
      ) : (
        <span className="emb-user__avatar emb-user__avatar--ph" aria-hidden="true">{initialsOf(user)}</span>
      )}
      <span className="emb-user__text">
        <span className="emb-user__name">{named ? user.displayName : `@${user.username}`}</span>
        {(named || badge) && (
          <span className="emb-user__sub">
            {named && <span className="emb-user__handle">@{user.username}</span>}
            {badge && (
              <span className="emb-identified" title={t('embed.visitors.identifiedTitle')}>
                <PatchCheckFill size={10} aria-hidden="true" />
                {t('embed.visitors.identified')}
              </span>
            )}
          </span>
        )}
      </span>
    </span>
  );
}

export function ProductCell({ product }) {
  const { t } = useLocale();
  // A product Outfino has not synced has no title or handle: name it by id.
  const title = product?.title || product?.handle
    || (product?.shopifyProductId ? t('embed.product.untitled', { id: product.shopifyProductId }) : '—');
  return (
    <span className="emb-product">
      {product?.imageUrl ? (
        <img className="emb-product__img" src={product.imageUrl} alt="" loading="lazy" />
      ) : (
        <span className="emb-product__img emb-product__img--ph" aria-hidden="true"><ImageIcon size={14} /></span>
      )}
      <span className={`emb-product__title${product?.title || product?.handle ? '' : ' emb-muted'}`}>{title}</span>
    </span>
  );
}

/// A compatibility % as a small meter + the number.
export function CompatMeter({ value }) {
  if (value == null) return <span className="emb-muted">—</span>;
  const v = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <span className="emb-compat">
      <span className="emb-compat__track" aria-hidden="true">
        <span className="emb-compat__fill" style={{ width: `${v}%` }} />
      </span>
      <span className="emb-compat__value">{v}%</span>
    </span>
  );
}

/// A product matching result (the API's visitor identity) as a chip:
/// the %, coloured by the same bands the shopper saw (80 / 60 / 40).
export function matchBand(percent) {
  if (percent >= 80) return 'high';
  if (percent >= 60) return 'good';
  if (percent >= 40) return 'part';
  return 'low';
}

export function MatchChip({ match, title }) {
  if (!match || !Number.isFinite(Number(match.percent))) return null;
  const v = Math.max(0, Math.min(100, Math.round(Number(match.percent))));
  return (
    <span className={`emb-match emb-match--${matchBand(v)}`} title={title}>
      <Stars size={10} aria-hidden="true" />
      {v}%
    </span>
  );
}

export function Segmented({ options, value, onChange, label, size }) {
  return (
    <div className={`emb-seg${size === 'sm' ? ' emb-seg--sm' : ''}`} role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`emb-seg__btn${value === o.value ? ' is-active' : ''}`}
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
